import { v4 } from "uuid";
import { randomEntry, randomKey } from "../utils";
import {
  IAffinityKey,
  IBitAction,
  IBitActionKey,
  IBitBaseConditionKey,
  IBitCondition,
  IBitConditionKey,
  IBitStatCondition,
  IBitStatusCondition,
  IBlackMagic,
  IGambit,
  IAbridgedGambit
} from "./types";

/**
 * Condition
 */

// prettier-ignore
export const baseComparators: IBitBaseConditionKey[] = ["equal", "notEqual", "always", "never"];
// prettier-ignore
export const comparators: IBitConditionKey[] = [...baseComparators, "greaterThan", "greaterThanOrEqual", "lessThan", "lessThanOrEqual"];

const generateStatCondition = (): IBitStatCondition => {
  const left: IBitStatCondition["left"] = "hp";
  const key = randomEntry(comparators);
  const right = Math.floor(Math.random() * 10) / 10;

  return { key, left, right };
};

const generateStatusCondition = (): IBitStatusCondition => {
  const left: IBitStatusCondition["left"] = "status";
  const key = randomEntry(baseComparators);
  const right: IBitStatusCondition["right"] = "poison";

  return { key, left, right };
};

export const generateCondition = (): IBitCondition =>
  Math.random() < 0.5 ? generateStatCondition() : generateStatusCondition();

export const fromAbridgedCondition = (
  abridgedCondition: IAbridgedGambit["bits"][1]
): IBitCondition => {
  let condition: IBitCondition;
  if (typeof abridgedCondition === "string")
    condition = { key: abridgedCondition, left: "hp", right: 0 };
  else condition = abridgedCondition;
  return condition;
};

/**
 * Action
 */

const generateBlackMagicAction = (
  key: IBlackMagic,
  affinity: IAffinityKey,
  cost = 2,
  intensity = 1
): IBitAction => ({
  key,
  type: "magic",
  subtype: "black",
  cost,
  intensity,
  affinity
});

export const gambitActions: Record<IBitActionKey, IBitAction> = {
  attack: { key: "attack", type: "attack" },

  // white magic
  heal: { key: "heal", type: "magic", subtype: "white", cost: 3 },
  refresh: { key: "refresh", type: "magic", subtype: "white", cost: 5 },

  // green magic
  protect: { key: "protect", type: "magic", subtype: "green", cost: 4 },
  shell: { key: "shell", type: "magic", subtype: "green", cost: 4 },
  poison: { key: "poison", type: "magic", subtype: "green", cost: 1 },
  sleep: { key: "sleep", type: "magic", subtype: "green", cost: 2 },
  blind: { key: "blind", type: "magic", subtype: "green", cost: 2 },

  // black magic
  ice: generateBlackMagicAction("ice", "ice"),
  ice1: generateBlackMagicAction("ice1", "ice", 3, 1.2),
  ice2: generateBlackMagicAction("ice2", "ice", 5, 1.4),
  fire: generateBlackMagicAction("fire", "fire"),
  fire1: generateBlackMagicAction("fire1", "fire", 3, 1.2),
  fire2: generateBlackMagicAction("fire2", "fire", 5, 1.4),
  earth: generateBlackMagicAction("earth", "earth"),
  earth1: generateBlackMagicAction("earth1", "earth", 4, 1.2),
  earth2: generateBlackMagicAction("earth2", "earth", 6, 1.4),
  water: generateBlackMagicAction("water", "water"),
  wind: generateBlackMagicAction("wind", "wind"),
  flame: generateBlackMagicAction("flame", "fire")
};

export const generateGambit = ({
  action = gambitActions[randomKey(gambitActions)],
  condition = generateCondition(),
  target = { key: "foe" },
  active = true,
  id = v4()
}: Partial<IGambit> = {}): IGambit => {
  return {
    id,
    target,
    condition,
    action,
    active
  };
};

export const generateDefaultGambit = (): IGambit => ({
  id: v4(),
  target: { key: "foe" },
  condition: { key: "always", left: "hp", right: 0 },
  action: gambitActions.attack,
  active: true
});
